import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Languages, Loader2, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { LANGUAGES } from "../../i18n/core";

const btnCls =
  "inline-flex items-center gap-2 border border-[#1A1A1A] px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase hover:bg-[#1A1A1A] hover:text-[#F9F6F0] transition-colors disabled:opacity-50";

export function useJobPoller(onDone) {
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  const track = (jobId) => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/translations/jobs/${jobId}`);
        setJob(data);
        if (data.status !== "running" && data.status !== "queued") {
          clearInterval(timer.current);
          onDone && onDone(data);
        }
      } catch (_e) {
        clearInterval(timer.current);
      }
    }, 2500);
  };
  const running = job && (job.status === "running" || job.status === "queued");
  return { job, setJob, track, running };
}

export default function AdminLangBar({ lang, onChange, kind, docKey, disabled = false, onTranslated }) {
  const [busy, setBusy] = useState(false);
  const { job, track, running } = useJobPoller((j) => {
    toast.success(`Translations updated (${j.done}/${j.total} languages)`);
  });
  const langName = LANGUAGES.find((l) => l.code === lang)?.name;

  const autoTranslate = async () => {
    if (!window.confirm(`Auto-translate all fields into ${langName} from English? This overwrites the current ${langName} text.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/translations/translate", { kind, key: docKey, langs: [lang], force: true });
      toast.success(`${data.translated} fields translated into ${langName}`);
      onTranslated && onTranslated();
    } catch (_e) {
      toast.error("Translation failed — please try again");
    } finally {
      setBusy(false);
    }
  };

  const translateAll = async () => {
    try {
      const { data } = await api.post("/admin/translations/translate", { kind, key: docKey });
      track(data.job_id);
      toast.success("Translating into all languages in the background…");
    } catch (_e) {
      toast.error("Could not start translation");
    }
  };

  return (
    <div data-testid="admin-lang-bar" className="border border-[#E5DFD5] bg-[#FFFFFF] p-5 mb-10 flex flex-wrap items-center gap-4">
      <Languages className="w-4 h-4 text-[#C5A880]" strokeWidth={1.5} />
      <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B]">Editing language</label>
      <select
        data-testid="admin-lang-select"
        value={lang}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="border border-[#E5DFD5] bg-[#F9F6F0] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A880] disabled:opacity-50"
      >
        {LANGUAGES.map((l) => (
          <option key={l.code} value={l.code}>
            {`${l.name} (${l.code.toUpperCase()})`}
          </option>
        ))}
      </select>
      {disabled && <span className="text-xs text-[#73706B]">Save in English first to add translations.</span>}
      {!disabled && lang !== "en" && (
        <button type="button" onClick={autoTranslate} disabled={busy} data-testid="admin-auto-translate-btn" className={btnCls}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />}
          {busy ? "Translating…" : "Auto-translate from English"}
        </button>
      )}
      {!disabled && lang === "en" && (
        <button type="button" onClick={translateAll} disabled={running} data-testid="admin-translate-all-btn" className={btnCls}>
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />}
          {running ? `Translating ${job.done}/${job.total}` : "Update all translations"}
        </button>
      )}
      {lang !== "en" && (
        <p data-testid="admin-lang-note" className="basis-full text-xs text-[#73706B] leading-relaxed">
          You are editing the <strong className="text-[#1A1A1A]">{langName}</strong> version. Only text is saved for this language —
          images, prices, links, slugs and categories are shared with English and must be changed in English. Empty fields fall back to English.
        </p>
      )}
      {lang === "en" && !disabled && (
        <p className="basis-full text-xs text-[#73706B] leading-relaxed">
          After saving English changes, click “Update all translations” to translate new or changed text into all 16 languages.
        </p>
      )}
    </div>
  );
}
